// packages/frontend/src/components/onboarding/OnboardingChecklistPanel.tsx

import { NAV_RAIL_WIDTH } from '../layout/nav/navRailWidth';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  IconButton,
  Portal,
  Progress,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { Fragment, useEffect, useRef, useState } from 'react';
import type { MagicLinkPurpose, OnboardingChecklistResponse } from '@bower/shared';
import { MaterialIcon } from '../ui/MaterialIcon';
import { OnboardingChecklistStep } from './OnboardingChecklistStep';
import { MobileCaptureQR } from './MobileCaptureQR';
import { AppInstallQR } from '../ui/AppInstallQR';
import {
  shouldShowChecklist,
  useDismissChecklist,
  useRecordExplicitCompletion,
} from '../../hooks/data/useOnboardingChecklist';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { ROUTES } from '../../constants/routes';
import { useModalCreateOpeners } from '../../hooks/create/useModalCreateOpeners';
import { openBirdChat } from '../../services/events/birdChatEvents';
import { useCanWrite } from '../../hooks/user/useWorkspaceRole';
import { useIsPhiWorkspace } from '../../hooks/user/useIsPhiWorkspace';
import { checklistStepViews, type ChecklistStepView } from './checklistSteps';
import { useChecklistStepActions } from './useChecklistStepActions';

/**
 * Ties the progress bar to its visible "N of M completed" caption via
 * aria-labelledby. A module constant rather than useId(): only one checklist
 * panel is ever mounted (it lives in a Portal), and a stable id keeps the
 * relationship greppable from either end.
 */
const onboardingProgressLabelId = 'onboarding-checklist-progress-label';

interface OnboardingChecklistPanelProps {
  isOpen: boolean;
  onClose: () => void;
  checklistData: OnboardingChecklistResponse | undefined;
  isMobile: boolean;
  sidebarWidth?: number;
}

/**
 * A desktop hand-off QR for one of the "On mobile" steps.
 *
 * ONE component for both steps, because all three gating conditions are class
 * rationale, not step-specific — gating only the step you happened to be
 * writing is how the sibling keeps the bug:
 *  - mobile already has a direct CTA, so it needs no QR;
 *  - a magic-link QR redeems into a full session, so it must never mount on an
 *    always-available surface — on desktop the panel stays mounted in a Portal
 *    and is merely hidden, so without the open check every user would mint a
 *    token on page load;
 *  - and each magic-link QR mints a real session token against a per-user
 *    budget, so a step the user has already done shouldn't keep spending it.
 *    (The magic link is now the browser fallback behind the app QR, so it
 *    mints only when the user picks the browser; these gates still hold.)
 *  - QRs mount only after an explicit reveal, and PanelContent allows only one
 *    reveal at a time, so the compact checklist never presents or mints two.
 *
 * A component rather than inline `&&`s also keeps the conditions out of
 * PanelContent's complexity budget.
 */
function HandoffQR({
  isMobile,
  isPanelOpen,
  isStepComplete,
  isExpanded,
  purpose,
  caption,
  browserCaption,
}: {
  readonly isMobile: boolean;
  readonly isPanelOpen: boolean;
  readonly isStepComplete: boolean;
  readonly isExpanded: boolean;
  readonly purpose: MagicLinkPurpose;
  readonly caption: string;
  readonly browserCaption: string;
}) {
  if (isMobile || !isPanelOpen || isStepComplete || !isExpanded) return null;
  // The app QR is what shows; the magic-link QR is its browser fallback and
  // mints a token only when the user asks for it.
  return (
    <AppInstallQR
      src={purpose === 'mobile_live' ? 'checklist_live' : 'checklist'}
      size={116}
      caption={caption}
      browserFallback={<MobileCaptureQR purpose={purpose} caption={browserCaption} />}
    />
  );
}

function handoffActionLabel(isMobile: boolean, mobileLabel: string, isExpanded: boolean): string {
  if (isMobile) return mobileLabel;
  return isExpanded ? 'Hide QR code' : 'Show QR code';
}

function MobileChecklistSteps({
  completions,
  isMobile,
  isOpen,
  onStartLive,
}: {
  readonly completions: OnboardingChecklistResponse['completions'];
  readonly isMobile: boolean;
  readonly isOpen: boolean;
  readonly onStartLive: () => void;
}) {
  const [expandedHandoff, setExpandedHandoff] = useState<MagicLinkPurpose | null>(null);

  useEffect(() => {
    if (!isOpen) setExpandedHandoff(null);
  }, [isOpen]);

  const toggleHandoff = (purpose: MagicLinkPurpose) => {
    setExpandedHandoff((current) => (current === purpose ? null : purpose));
  };
  const startCapture = () => {
    safeAnalytics.track(ANALYTICS_EVENTS.ONBOARDING_MOBILE_CAPTURE_STARTED);
    window.open(`${window.location.origin}/m/capture`, '_blank');
  };
  const startLive = () => {
    onStartLive();
    safeAnalytics.track(ANALYTICS_EVENTS.ONBOARDING_LIVE_TEASER_CLICKED);
    window.open(`${window.location.origin}${ROUTES.mobileLive}`, '_blank');
  };

  const captureExpanded = expandedHandoff === 'mobile_capture';
  const liveExpanded = expandedHandoff === 'mobile_live';

  return (
    <VStack spacing={0} align="stretch">
      <OnboardingChecklistStep
        stepKey="mobile_capture"
        label="Photograph your lab or notebook"
        description={
          isMobile
            ? 'Bower reads your lab in seconds'
            : 'Point your phone camera at anything on your bench: Bower will read it instantly.'
        }
        isComplete={completions.mobile_capture}
        completionMethod="explicit"
        ctaLabel={handoffActionLabel(isMobile, 'Capture now', captureExpanded)}
        onCtaClick={isMobile ? startCapture : () => toggleHandoff('mobile_capture')}
      />
      <HandoffQR
        isMobile={isMobile}
        isPanelOpen={isOpen}
        isStepComplete={completions.mobile_capture}
        isExpanded={captureExpanded}
        purpose="mobile_capture"
        caption="Scan to get the Bower app on your phone"
        browserCaption="Scan to open in your phone's browser: you'll be signed in automatically"
      />

      {/* Live is a phone-only mode — the desktop Bower panel has no Live
          button to point at, so desktop gets a magic-link QR rather than a
          CTA with nowhere to go. The QR encodes /m/capture?token= (the
          redemption route); the token's stored purpose is what lands the
          phone on /m/bird, where the Live button lives. */}
      <OnboardingChecklistStep
        stepKey="live_agent_teaser"
        label={isMobile ? 'Try live vision agent' : 'Try live agent mode'}
        description={
          isMobile
            ? 'Hands-free voice & vision'
            : 'Talk to Bower hands-free while it watches your bench through your phone camera.'
        }
        isComplete={completions.live_agent_teaser}
        completionMethod="explicit"
        ctaLabel={handoffActionLabel(isMobile, 'Try now', liveExpanded)}
        onCtaClick={isMobile ? startLive : () => toggleHandoff('mobile_live')}
      />
      <HandoffQR
        isMobile={isMobile}
        isPanelOpen={isOpen}
        isStepComplete={completions.live_agent_teaser}
        isExpanded={liveExpanded}
        purpose="mobile_live"
        caption="Scan to get the Bower app, then start Live from its capture menu"
        browserCaption="Scan to start a live session in your phone's browser"
      />
    </VStack>
  );
}

/**
 * The rail's compact view of the unified setup list (checklist-2026-11): the
 * same stamped steps the dashboard list shows, from the same table, as a
 * checklist. Never opens itself; the dashboard is the main surface.
 */
function UnifiedPanelContent({
  data,
  onClose,
  isMobile,
  isOpen,
}: {
  data: OnboardingChecklistResponse;
  onClose: () => void;
  isMobile: boolean;
  isOpen: boolean;
}) {
  const canWrite = useCanWrite();
  const isPhi = useIsPhiWorkspace();
  const [showPhoneQr, setShowPhoneQr] = useState(false);
  const { actions, labelFor } = useChecklistStepActions(data, 'onboarding_checklist', () =>
    setShowPhoneQr((shown) => !shown),
  );

  useEffect(() => {
    if (!isOpen) setShowPhoneQr(false);
  }, [isOpen]);

  // The Ask step's listener lives in useChecklistPanel, which is mounted
  // whether or not this panel is open (a closed Drawer mounts nothing).

  const unified = data.unified;
  if (!unified) return null;
  const context = { canWrite, isPhi, hasStartHere: !!unified.startHere };
  const steps = [
    ...checklistStepViews(unified.coreSteps, unified.variant, context),
    ...checklistStepViews(unified.bonusSteps, unified.variant, context),
  ];

  const run = (step: ChecklistStepView) => {
    actions[step.action]();
    if (step.action !== 'phone' || isMobile) onClose();
  };
  const ctaLabel = (step: ChecklistStepView) => {
    if (step.action !== 'phone' || isMobile) return labelFor(step);
    return showPhoneQr ? 'Hide QR code' : 'Show QR code';
  };

  return (
    <VStack spacing={0} align="stretch">
      <HStack justify="space-between" px={4} pt={4} pb={2}>
        <Text fontWeight="semibold" color="text.primary" fontSize="sm">
          Get set up
        </Text>
        <IconButton
          aria-label="Close panel"
          icon={<MaterialIcon name="close" />}
          size="sm"
          minW="44px"
          minH="44px"
          variant="ghost"
          onClick={onClose}
        />
      </HStack>
      <VStack spacing={1} px={4} pb={3}>
        <Progress
          value={(data.completedCount / Math.max(1, data.totalCoreSteps)) * 100}
          aria-labelledby={onboardingProgressLabelId}
          borderRadius="full"
          size="sm"
          w="full"
          bg="border.default"
        />
        <Text
          id={onboardingProgressLabelId}
          fontSize="xs"
          color="text.muted"
          alignSelf="flex-start"
        >
          {data.completedCount} of {data.totalCoreSteps} done
        </Text>
      </VStack>
      <VStack spacing={0} px={4} pb={4} align="stretch">
        {steps.map((step) => (
          <Fragment key={step.key}>
            <OnboardingChecklistStep
              stepKey={step.key}
              label={step.title}
              description={step.description}
              isComplete={data.completions[step.key]}
              completionMethod={
                step.action === 'ask' || step.action === 'start_here' ? 'explicit' : 'derived'
              }
              variant={unified.variant}
              ctaLabel={ctaLabel(step)}
              onCtaClick={() => run(step)}
            />
            <HandoffQR
              isMobile={isMobile}
              isPanelOpen={isOpen}
              isStepComplete={data.completions[step.key]}
              isExpanded={step.action === 'phone' && showPhoneQr}
              purpose="mobile_capture"
              caption="Scan to get the Bower app on your phone"
              browserCaption="Scan to open in your phone's browser: you'll be signed in automatically"
            />
          </Fragment>
        ))}
      </VStack>
    </VStack>
  );
}

function PanelContent({
  data,
  onOpenImport,
  onOpenBird,
  onOpenCollection,
  onOpenInvite,
  onOpenEquipment,
  onClose,
  isMobile,
  isOpen,
}: {
  data: OnboardingChecklistResponse;
  onOpenImport: () => void;
  onOpenBird: () => void;
  onOpenCollection: () => void;
  onOpenInvite: () => void;
  onOpenEquipment: () => void;
  onClose: () => void;
  isMobile: boolean;
  isOpen: boolean;
}) {
  const confirmDialog = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const dismissChecklist = useDismissChecklist();
  const recordExplicit = useRecordExplicitCompletion();

  // Auto-dismiss 5 seconds after all steps complete, in case the user closes the panel
  // without clicking "Done". The timer resets if the panel re-mounts.
  useEffect(() => {
    if (!data.allCoreStepsComplete) return;
    const timer = setTimeout(() => {
      dismissChecklist.mutate(undefined, { onSuccess: onClose });
    }, 5000);
    return () => clearTimeout(timer);
  }, [data.allCoreStepsComplete, dismissChecklist.mutate, onClose]);

  // Record ask_bird completion when the user actually sends a Bird message (not just opens chat)
  useEffect(() => {
    if (data.completions.ask_bird) return; // already complete, skip listener
    const handleMessageSent = () => {
      recordExplicit.mutate('ask_bird_clicked');
    };
    window.addEventListener('onboarding:birdMessageSent', handleMessageSent);
    return () => window.removeEventListener('onboarding:birdMessageSent', handleMessageSent);
  }, [data.completions.ask_bird, recordExplicit.mutate]);

  const handleConfirmDismiss = () => {
    dismissChecklist.mutate(undefined, { onSuccess: onClose });
    confirmDialog.onClose();
  };

  const progressValue = (data.completedCount / data.totalCoreSteps) * 100;

  // v2 (checklist-2026-07): endowed-progress identity step, create_collection
  // dropped, invite moved to a bonus row below the mobile section.
  const isV2 = data.state?.enrolledVersion === 'checklist-2026-07';

  const inviteStep = (
    <OnboardingChecklistStep
      stepKey="invite_teammate"
      label="Invite a teammate"
      description={
        isV2 ? 'Optional: collaborate on research together' : 'Collaborate on research together'
      }
      isComplete={data.completions.invite_teammate}
      completionMethod="derived"
      ctaLabel="Invite"
      onCtaClick={onOpenInvite}
      onSkipClick={() => {
        recordExplicit.mutate('invite_teammate_skipped');
        safeAnalytics.track(ANALYTICS_EVENTS.ONBOARDING_INVITE_SKIPPED);
      }}
    />
  );

  if (data.allCoreStepsComplete) {
    return (
      <VStack spacing={4} p={4} align="center" textAlign="center">
        <Text fontSize="2xl">🎉</Text>
        <Text fontWeight="bold" color="text.primary">
          You&apos;re all set!
        </Text>
        <Text fontSize="sm" color="text.secondary">
          Bower is loaded and ready. It knows your research. Go explore.
        </Text>
        <Button
          size="sm"
          onClick={() => dismissChecklist.mutate(undefined, { onSuccess: onClose })}
          isLoading={dismissChecklist.isPending}
        >
          Done, hide this guide
        </Button>
      </VStack>
    );
  }

  return (
    <VStack spacing={0} align="stretch">
      {/* Header */}
      <HStack justify="space-between" px={4} pt={4} pb={2}>
        <Text fontWeight="semibold" color="text.primary" fontSize="sm">
          Getting started
        </Text>
        <IconButton
          aria-label="Close panel"
          icon={<MaterialIcon name="close" />}
          size="xs"
          variant="ghost"
          onClick={onClose}
        />
      </HStack>

      {/* Progress */}
      <VStack spacing={1} px={4} pb={3}>
        {/* An ARIA progressbar with no accessible name is announced as just
            "progress bar" with a percentage and no subject (WCAG 4.1.2 / axe
            aria-progressbar-name, serious). The visible count below is the
            name a sighted user reads, so point at it rather than duplicating
            the string in an aria-label that could drift out of step. */}
        <Progress
          value={progressValue}
          aria-labelledby={onboardingProgressLabelId}
          borderRadius="full"
          size="sm"
          w="full"
          bg="border.default"
        />
        <Text
          id={onboardingProgressLabelId}
          fontSize="xs"
          color="text.muted"
          alignSelf="flex-start"
        >
          {data.completedCount} of {data.totalCoreSteps} completed
        </Text>
      </VStack>

      {/* Core steps. The set shown adapts to the enrolled version: v2
          (checklist-2026-07) leads with the pre-completed identity step
          for endowed progress and drops "create a collection"; v1 keeps
          its original set. The progress bar/count come from the backend,
          which computes them against the enrolled config either way. */}
      <VStack spacing={0} px={4} align="stretch">
        {isV2 ? (
          // v2 leads with the pre-completed identity step (endowed
          // progress). workspace_created isn't a v2 core step, so it's not
          // shown here — rendering it would put two green ticks against a
          // "1 of 5" count.
          <OnboardingChecklistStep
            stepKey="bird_knows_you"
            label="Bower knows who you are"
            description="From your account setup"
            isComplete
            completionMethod="derived"
          />
        ) : (
          <OnboardingChecklistStep
            stepKey="workspace_created"
            label="Create your workspace"
            isComplete={data.completions.workspace_created}
            completionMethod="derived"
          />
        )}
        <OnboardingChecklistStep
          stepKey="import_first_file"
          label={isV2 ? 'Add a protocol or method' : 'Give Bower something to read'}
          description="Unlock Bower's best answers"
          isComplete={data.completions.import_first_file}
          completionMethod="derived"
          ctaLabel="Import"
          onCtaClick={onOpenImport}
        />
        <OnboardingChecklistStep
          stepKey="ask_bird"
          label="Ask Bower a question"
          description="AI trained on your work"
          isComplete={data.completions.ask_bird}
          completionMethod="explicit"
          ctaLabel="Open chat"
          onCtaClick={onOpenBird}
        />
        {/* v2 core step: photograph an instrument. Derived — auto-ticks once
            the workspace holds an equipment artifact. */}
        {isV2 && (
          <OnboardingChecklistStep
            stepKey="capture_equipment"
            label="Photograph lab equipment"
            description="Bower reads its manual so it can help you operate it"
            isComplete={data.completions.capture_equipment}
            completionMethod="derived"
            ctaLabel="Add equipment"
            onCtaClick={onOpenEquipment}
          />
        )}
        {!isV2 && (
          <OnboardingChecklistStep
            stepKey="create_collection"
            label="Create a collection"
            description="Group related work"
            isComplete={data.completions.create_collection}
            completionMethod="derived"
            ctaLabel="Create"
            onCtaClick={onOpenCollection}
          />
        )}
        {/* v1: invite is a core step here. v2: rendered as a bonus below. */}
        {!isV2 && inviteStep}
      </VStack>

      {/* On mobile section */}
      <Box px={4} pt={4}>
        <HStack spacing={2} mb={3}>
          <Divider />
          <Text fontSize="xs" color="text.muted" textTransform="uppercase" whiteSpace="nowrap">
            On mobile
          </Text>
          <Divider />
        </HStack>
        <MobileChecklistSteps
          completions={data.completions}
          isMobile={isMobile}
          isOpen={isOpen}
          onStartLive={() => recordExplicit.mutate('live_agent_teaser_clicked')}
        />
      </Box>

      {/* v2 bonus: invite is optional (not counted toward core completion),
          so it sits below the mobile section rather than in the core list. */}
      {isV2 && (
        <Box px={4} pt={4}>
          <HStack spacing={2} mb={3}>
            <Divider />
            <Text fontSize="xs" color="text.muted" textTransform="uppercase" whiteSpace="nowrap">
              Bonus
            </Text>
            <Divider />
          </HStack>
          <VStack spacing={0} align="stretch">
            {inviteStep}
          </VStack>
        </Box>
      )}

      {/* Footer */}
      <VStack spacing={0} align="stretch" px={4} pb={3} mt={2}>
        <Button
          variant="ghost-danger"
          size="xs"
          alignSelf="center"
          mt={1}
          onClick={confirmDialog.onOpen}
        >
          Dismiss forever
        </Button>
      </VStack>

      {/* Dismiss confirmation.
          The panel renders at `zIndex="overlay"` (1300). Chakra's default
          modal layer is `modal` (1400), which would sit above the panel
          but below our `skipLink` (1600) override here — so the confirm
          dialog still wins over both the panel and any concurrently open
          modal. Toast at 1700 still beats everything. */}
      <AlertDialog
        isOpen={confirmDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={confirmDialog.onClose}
        isCentered
      >
        <AlertDialogOverlay zIndex="skipLink">
          <AlertDialogContent containerProps={{ zIndex: 'skipLink' }}>
            <AlertDialogHeader fontSize="md" fontWeight="bold">
              Hide this guide?
            </AlertDialogHeader>
            <AlertDialogCloseButton ref={cancelRef} />
            <AlertDialogBody fontSize="sm" color="text.secondary">
              You won&apos;t see it again. You can still access the help center any time from the
              menu.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                size="sm"
                variant="danger"
                onClick={handleConfirmDismiss}
                isLoading={dismissChecklist.isPending}
              >
                Yes, hide it
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
}

export function OnboardingChecklistPanel({
  isOpen,
  onClose,
  checklistData,
  isMobile,
  sidebarWidth = NAV_RAIL_WIDTH,
}: OnboardingChecklistPanelProps) {
  // The create CTAs: the Create modal at that type, aimed at what is in view.
  // Above the early return, as a hook must be.
  const create = useModalCreateOpeners(undefined, 'onboarding_checklist');

  // Gate on dismissal too, not just data presence: PanelContent mounts a
  // window listener that POSTs /onboarding-checklist/explicit-completion on
  // every Bird message, plus an auto-dismiss timer — neither should exist for
  // a user who already dismissed the checklist. (The panel was invisible for
  // them, but the listeners still fired network calls.)
  if (!checklistData) return null;
  const unified = checklistData.unified;
  if (unified ? unified.dismissedAt : !shouldShowChecklist(checklistData.state)) return null;

  const handleOpenImport = () => {
    create.onOpenImport();
    onClose();
  };
  const handleOpenBird = () => {
    // freshContext explicitly: this checklist step exists to give the user a
    // first conversation with Bird, so it starts one rather than dropping them
    // into a thread already in progress.
    openBirdChat({ source: 'onboarding_checklist', freshContext: true });
    window.dispatchEvent(new CustomEvent('onboarding:askBirdOpened'));
    onClose();
  };
  const handleOpenCollection = () => {
    create.onOpenCollection();
    onClose();
  };
  const handleOpenInvite = () => {
    window.dispatchEvent(new CustomEvent('onboarding:openInvite'));
    onClose();
  };
  const handleOpenEquipment = () => {
    create.onOpenEquipment();
    onClose();
  };

  const content = unified ? (
    <UnifiedPanelContent
      data={checklistData}
      onClose={onClose}
      isMobile={isMobile}
      isOpen={isOpen}
    />
  ) : (
    <PanelContent
      data={checklistData}
      onOpenImport={handleOpenImport}
      onOpenBird={handleOpenBird}
      onOpenCollection={handleOpenCollection}
      onOpenInvite={handleOpenInvite}
      onOpenEquipment={handleOpenEquipment}
      onClose={onClose}
      isMobile={isMobile}
      isOpen={isOpen}
    />
  );

  if (isMobile) {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} placement="bottom">
        <DrawerOverlay />
        <DrawerContent borderTopRadius="xl" maxH="80dvh" overflowY="auto">
          <DrawerHeader p={0} />
          <DrawerBody p={0}>{content}</DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  // Render as a Portal + fixed-position panel so it escapes the sidebar's overflow:hidden.
  // The invisible trigger approach with Popover caused the panel to be clipped at the
  // sidebar's right edge because Popper positioned content relative to x=0 (sidebar left).
  //
  // z-index: panel at `overlay` (1300) so Chakra modals at `modal` (1400) naturally
  // sit above it. The dismiss AlertDialog is bumped to `skipLink` (1600) so it still
  // appears on top of the panel even though the panel is now below the modal layer.
  return (
    <Portal>
      {/* Transparent backdrop — click outside to close */}
      {isOpen && <Box position="fixed" inset={0} zIndex="sticky" onClick={onClose} />}
      {/* Panel anchored to the sidebar's right edge */}
      <Box
        position="fixed"
        left={`${sidebarWidth}px`}
        top={0}
        bottom={0}
        w="340px"
        overflowY="auto"
        bg="bg.surface"
        border="1px"
        borderColor="border.default"
        shadow="lg"
        zIndex="overlay"
        transform={isOpen ? 'translateX(0)' : 'translateX(-20px)'}
        opacity={isOpen ? 1 : 0}
        pointerEvents={isOpen ? 'auto' : 'none'}
        transition="transform 0.2s ease, opacity 0.15s ease"
      >
        {content}
      </Box>
    </Portal>
  );
}
